import React, { useState, useEffect } from 'react';
import { 
    Box, Dialog, DialogTitle, DialogContent, Button, DialogActions, Typography, 
    Table, TableBody, TableCell, TableHead, TableRow, Grid, Card, CardMedia, CircularProgress, Tabs, Tab, Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getUnitById, referenceTypes } from '../../utils/Enums';
import { getReferences } from '../../services/SuppliesService';
import ProductImagesService from '../../services/ProductImagesService';
import { getProductById, getProductPriceHistory } from '../../services/ProductService';
import { StockHistoryContent } from '../StockHistory/StockHistoryModal';
import { REFERENCE_TYPE } from '../../services/StockEventService';
import { shouldLoadFoodProductReferences } from '../../utils/ProductTypeUtils';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { formatMoney } from '../../utils/currency';

// Componente reutilizable para secciones de tabla (usa referenceName del API cuando viene en el detalle)
const ProductTableSection = ({ title, items, t, getReferenceNameById }) => {
    if (!items || items.length === 0) return null;

    const getName = (item) =>
        (item.referenceName && item.referenceName.trim() !== '')
            ? item.referenceName
            : getReferenceNameById(item.referenceId1, item.referenceId2, item.referenceType);

    return (
        <>
            <Typography gutterBottom variant="h6" style={{ marginTop: '20px' }}>
                <span className="roboto-regular">{t(title)}</span>
            </Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell width="15%"><span className="roboto-regular">{t('quantity')}</span></TableCell>
                        <TableCell width="15%"><span className="roboto-regular">{t('unit')}</span></TableCell>
                        <TableCell width="30%"><span className="roboto-regular">{t('reference_type')}</span></TableCell>
                        <TableCell width="40%"><span className="roboto-regular">{t('reference')}</span></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item, index) => (
                        <TableRow key={index}>
                            <TableCell><span className="roboto-light">{item.quantity}</span></TableCell>
                            <TableCell><span className="roboto-light">{getUnitById(item.unit).label}</span></TableCell>
                            <TableCell>
                                <span className="roboto-light">
                                    {item.referenceType === referenceTypes.INSUMO ? t('supply') : item.referenceType === referenceTypes.PREPARADO ? t('preparation') : t('product')}
                                </span>
                            </TableCell>
                            <TableCell>
                                <span className="roboto-light">{getName(item)}</span>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    );
};

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} id={`product-tabpanel-${index}`} aria-labelledby={`product-tab-${index}`}>
        {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
);

const ViewProductModal = ({ locationId, open, onClose, product, canViewStockHistory = false, canViewCost = false }) => {
    const { t } = useTranslation();
    const [references, setReferences] = useState([]);
    const [productImages, setProductImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [fullProduct, setFullProduct] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [tab, setTab] = useState(0);
    const [priceHistory, setPriceHistory] = useState(null);
    const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);

    // Siempre usar el detalle completo cuando esté cargado; así Contenido/Packaging muestran los nombres correctos
    const displayProduct = fullProduct || product;
    const waitingForDetail = open && product?.id && !fullProduct && loadingDetail;

    const stockControlMode = displayProduct?.stockControlMode || displayProduct?.stock_control_mode ||
        (displayProduct?.tubIceCream ? 'FRACTIONAL' : 'UNITARY');
    const canLoadFoodReferences = shouldLoadFoodProductReferences(
        localStorage.getItem('companyType'),
        product?.productType
    );

    useEffect(() => {
        if (!open) {
            setFullProduct(null);
            setLoadingDetail(false);
            setTab(0);
            setPriceHistory(null);
            setLoadingPriceHistory(false);
            return;
        }
        if (canLoadFoodReferences) {
            getReferences(locationId, { skipGlobalErrorRedirect: true })
                .then((data) => setReferences(data || []))
                .catch((error) => {
                    console.error('Error loading product references:', error);
                    setReferences([]);
                });
        } else {
            setReferences([]);
        }
        if (product?.id) {
            setFullProduct(null);
            setLoadingDetail(true);
            setPriceHistory(null);
            getProductById(product.id, locationId)
                .then((data) => {
                    setFullProduct(data || null);
                })
                .catch(() => setFullProduct(null))
                .finally(() => setLoadingDetail(false));
            loadProductImages(product.id);
        } else {
            setFullProduct(null);
            setLoadingDetail(false);
        }
    }, [open, product?.id, locationId, canLoadFoodReferences]);

    const supportsStockHistory = canViewStockHistory &&
        (displayProduct?.productType === 'DEFINED' || displayProduct?.productType === 'RETAIL');
    const priceHistoryTabIndex = supportsStockHistory ? 2 : 1;

    useEffect(() => {
        if (!open || tab !== priceHistoryTabIndex || !product?.id) return;

        let cancelled = false;
        setLoadingPriceHistory(true);
        getProductPriceHistory(product.id, locationId)
            .then((data) => {
                if (!cancelled) setPriceHistory(data || { cost: [], salePrice: [] });
            })
            .catch((error) => {
                console.error('Error loading product price history:', error);
                if (!cancelled) setPriceHistory({ cost: [], salePrice: [] });
            })
            .finally(() => {
                if (!cancelled) setLoadingPriceHistory(false);
            });

        return () => { cancelled = true; };
    }, [open, tab, priceHistoryTabIndex, product?.id, locationId]);

    const loadProductImages = async (productId) => {
        try {
            setLoadingImages(true);
            const images = await ProductImagesService.getProductImages(productId);
            setProductImages(images || []);
        } catch (error) {
            console.error('Error loading product images:', error);
            setProductImages([]);
        } finally {
            setLoadingImages(false);
        }
    };

    const getReferenceNameById = (referenceId1, referenceId2, type) => { 
        if (type === referenceTypes.CATEGORIES) {
            const ref = references.find(r => 
                r.referenceId1 === referenceId1 && r.referenceId2 === referenceId2 && r.referenceType === type
            );
            return ref ? `${ref.referenceName1} - ${ref.referenceName2}` : t('not_defined');
        } 
    
        const ref = references.find(r => r.referenceId1 === referenceId1 && r.referenceType === type);
        return ref ? ref.referenceName1 : t('not_defined');
    };

    const getReferenceTypeLabel = (type) => {
        if (type === referenceTypes.INSUMO) return t('supply');
        if (type === referenceTypes.PREPARADO) return t('preparation');
        if (type === referenceTypes.PRODUCTS) return t('product');
        return String(type);
    };

    const priceChartData = (() => {
        const points = new Map();
        const addPoints = (history, key) => (history || []).forEach(({ date, price }) => {
            const timestamp = new Date(date).getTime();
            if (Number.isNaN(timestamp)) return;
            const point = points.get(timestamp) || { timestamp, date: new Date(date).toLocaleDateString('es-UY') };
            point[key] = price;
            points.set(timestamp, point);
        });
        if (canViewCost) addPoints(priceHistory?.cost, 'cost');
        addPoints(priceHistory?.salePrice, 'salePrice');
        return [...points.values()].sort((a, b) => a.timestamp - b.timestamp);
    })();

    return (    
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('view_product')}</DialogTitle>
            <DialogContent dividers>
                <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="product tabs" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label={t('product') || 'Producto'} id="product-tab-0" aria-controls="product-tabpanel-0" />
                    {supportsStockHistory && <Tab label={t('stock_history') || 'Historial de stock'} id="product-tab-1" aria-controls="product-tabpanel-1" />}
                    <Tab label="Historial de precios" id={`product-tab-${priceHistoryTabIndex}`} aria-controls={`product-tabpanel-${priceHistoryTabIndex}`} />
                </Tabs>
                <Divider />
                <TabPanel value={tab} index={0}>
                {!displayProduct || waitingForDetail ? (
                    <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                ) : (
                <>
                <Typography gutterBottom variant="h6"><span className="roboto-regular">{t('basic_information')}</span></Typography>
                <Grid container spacing={2}>
                    <Grid item sm={5}>
                        {[
                            { label: t('name'), value: displayProduct.name },
                            displayProduct.alias && { label: t('product_alias'), value: displayProduct.alias },
                            { label: t('price'), value: formatMoney(displayProduct.price, displayProduct.priceCurrency) },
                            { label: t('category'), value: displayProduct.category?.name || null },
                            displayProduct.subCategory && { label: t('sub_category'), value: displayProduct.subCategory?.name || null },
                            { label: t('stock_control_mode'), value: stockControlMode === 'FRACTIONAL' ? t('stock_control_mode_fractional') : t('stock_control_mode_unitary') },

                        ].filter(Boolean).map(({ label, value }) => (
                            <Typography key={label}>
                                <span className="roboto-regular">{`${label}:`}</span> <span className="roboto-light">{value}</span>
                            </Typography>
                        ))}
                    </Grid>
                    <Grid item sm={5}>
                    {[
                        (displayProduct.productType === 'DEFINED' || displayProduct.productType === 'RETAIL') && { label: t('stock'), value: displayProduct.stock },
                        (displayProduct.productType === 'DEFINED' || displayProduct.productType === 'RETAIL') && { label: t('minimum_stock'), value: displayProduct.minimumStock },
                        (displayProduct.productType === 'DEFINED' || displayProduct.productType === 'RETAIL') && { label: t('suggested_stock'), value: displayProduct.suggestedStock },
                        { label: t('product_type') || 'Tipo de producto', value: displayProduct.productType === 'DEFINED' ? (t('product_type_defined') || 'Definido') : displayProduct.productType === 'UNDEFINED' ? (t('product_type_undefined') || 'Indefinido') : (t('product_type_retail') || 'Retail') },
                        displayProduct.sku && { label: t('barcode'), value: displayProduct.sku },
                        displayProduct.productType === 'RETAIL' && displayProduct.primarySupplier && { label: t('primary_supplier') || 'Proveedor Principal', value: displayProduct.primarySupplier.name || (displayProduct.primarySupplier.businessName ? `${displayProduct.primarySupplier.name} - ${displayProduct.primarySupplier.businessName}` : 'N/A') },
                        displayProduct.productType === 'RETAIL' && displayProduct.secondarySupplier && { label: t('secondary_supplier') || 'Proveedor Secundario', value: displayProduct.secondarySupplier.name || (displayProduct.secondarySupplier.businessName ? `${displayProduct.secondarySupplier.name} - ${displayProduct.secondarySupplier.businessName}` : 'N/A') },
                        displayProduct.productType === 'UNDEFINED' && displayProduct.maxOptionalSelections != null && { label: t('max_optional_selections') || 'Máximos opcionales a elegir', value: displayProduct.maxOptionalSelections },
                    ]
                        .filter(Boolean)
                        .map(({ label, value }) => (
                        <Typography key={label}>
                            <span className="roboto-regular">{`${label}:`}</span>{' '}
                            <span className="roboto-light">{value}</span>
                        </Typography>
                        ))}
                    </Grid>
                </Grid>

                {/* Imágenes del producto */}
                {productImages.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography gutterBottom variant="h6">
                            <span className="roboto-regular">{t('images') || 'Imágenes'}</span>
                        </Typography>
                        {loadingImages ? (
                            <Box display="flex" justifyContent="center" p={2}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {/* Mostrar imágenes nuevas si existen */}
                                {productImages.map((img, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={img.id}>
                                        <Card>
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={img.imageUrl}
                                                alt={`Imagen ${index + 1}`}
                                                sx={{ objectFit: 'cover' }}
                                            />
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                )}

                {/* Reutilización del componente de tabla */}
                <ProductTableSection title="toppings" items={displayProduct.toppings} t={t} getReferenceNameById={getReferenceNameById} />
                <ProductTableSection title="content" items={displayProduct.content} t={t} getReferenceNameById={getReferenceNameById} />
                <ProductTableSection title="packaging" items={displayProduct.packaging} t={t} getReferenceNameById={getReferenceNameById} />
                <ProductTableSection title="defined_content" items={displayProduct.definedContent} t={t} getReferenceNameById={getReferenceNameById} />
                <ProductTableSection title="undefined_content" items={displayProduct.undefinedContent} t={t} getReferenceNameById={getReferenceNameById} />

                {/* Opcionales (solo UNDEFINED) */}
                {displayProduct.productType === 'UNDEFINED' && displayProduct.optionals?.length > 0 && (
                    <>
                        <Typography gutterBottom variant="h6" style={{ marginTop: '20px' }}>
                            <span className="roboto-regular">{t('optionals')}</span>
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%"><span className="roboto-regular">{t('type') || 'Tipo'}</span></TableCell>
                                    <TableCell width="35%"><span className="roboto-regular">{t('reference')}</span></TableCell>
                                    <TableCell width="15%"><span className="roboto-regular">{t('quantity')}</span></TableCell>
                                    <TableCell width="15%"><span className="roboto-regular">{t('unit')}</span></TableCell>
                                    <TableCell width="15%"><span className="roboto-regular">{t('extra_price') || 'Precio extra'}</span></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayProduct.optionals.map((opt, index) => (
                                    <TableRow key={index}>
                                        <TableCell><span className="roboto-light">{getReferenceTypeLabel(opt.referenceType)}</span></TableCell>
                                        <TableCell><span className="roboto-light">{getReferenceNameById(opt.referenceId1, null, opt.referenceType)}</span></TableCell>
                                        <TableCell><span className="roboto-light">{opt.quantity ?? 1}</span></TableCell>
                                        <TableCell><span className="roboto-light">{getUnitById(opt.unit ?? 5).label}</span></TableCell>
                                        <TableCell><span className="roboto-light">{opt.extraPrice != null ? opt.extraPrice : '—'}</span></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
                </>
                )}
                </TabPanel>
                {supportsStockHistory && (
                    <TabPanel value={tab} index={1}>
                        <StockHistoryContent
                            active={tab === 1}
                            locationId={locationId}
                            referenceType={REFERENCE_TYPE.PRODUCT}
                            referenceId={displayProduct?.id}
                        />
                    </TabPanel>
                )}
                <TabPanel value={tab} index={priceHistoryTabIndex}>
                    <Typography variant="h6" gutterBottom>Historial de precios</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Evolución del costo de reposición y del precio de venta en esta sucursal.
                    </Typography>
                    {loadingPriceHistory ? (
                        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                    ) : priceChartData.length > 0 ? (
                        <Box sx={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <LineChart data={priceChartData} margin={{ top: 12, right: 20, bottom: 8, left: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis tickFormatter={(value) => `${formatMoney(Number(value))}`} />
                                    <ChartTooltip formatter={(value) => `${formatMoney(Number(value))}`} />
                                    <Legend />
                                    {canViewCost && <Line type="stepAfter" dataKey="cost" name="Costo" stroke="#7b1fa2" strokeWidth={2} connectNulls />}
                                    <Line type="stepAfter" dataKey="salePrice" name="Precio de venta" stroke="#1976d2" strokeWidth={2} connectNulls />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    ) : (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <Typography color="text.secondary">Todavía no hay cambios de precio registrados.</Typography>
                        </Box>
                    )}
                </TabPanel>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewProductModal;
